import { get, set, del, keys } from "idb-keyval";

const OFFLINE_PREFIX = "prezntz-offline:";

export interface OfflineSavedGift {
  id: string;
  name: string;
  description: string;
  priceMin: number;
  priceMax: number;
  imageUrl?: string;
  tags: string[];
  reasoning: string;
  savedAt: string;
}

export interface OfflineSearchResult {
  searchId: string;
  recipientName: string;
  occasion: string;
  recommendations: OfflineSavedGift[];
  cachedAt: string;
}

export async function saveSavedGiftsOffline(gifts: OfflineSavedGift[]): Promise<void> {
  await set(`${OFFLINE_PREFIX}saved-gifts`, gifts);
}

export async function getSavedGiftsOffline(): Promise<OfflineSavedGift[]> {
  return (await get(`${OFFLINE_PREFIX}saved-gifts`)) ?? [];
}

export async function cacheSearchResult(result: OfflineSearchResult): Promise<void> {
  const existing = await getCachedSearchResults();
  // Keep last 5 searches
  const updated = [result, ...existing.filter(r => r.searchId !== result.searchId)].slice(0, 5);
  await set(`${OFFLINE_PREFIX}search-results`, updated);
}

export async function getCachedSearchResults(): Promise<OfflineSearchResult[]> {
  return (await get(`${OFFLINE_PREFIX}search-results`)) ?? [];
}

export async function clearOfflineCache(): Promise<void> {
  const allKeys = await keys();
  for (const key of allKeys) {
    if (typeof key === "string" && key.startsWith(OFFLINE_PREFIX)) {
      await del(key);
    }
  }
}

export async function isOffline(): Promise<boolean> {
  return !navigator.onLine;
}

// ===== Offline Mutation Queue =====

export interface OfflineMutation {
  id: string;
  timestamp: number;
  method: "POST" | "PUT" | "DELETE";
  url: string;
  body?: unknown;
  description: string; // Human-readable, e.g. "Update contact Sam"
}

const MUTATION_QUEUE_KEY = `${OFFLINE_PREFIX}mutation-queue`;

export async function getMutationQueue(): Promise<OfflineMutation[]> {
  return (await get(MUTATION_QUEUE_KEY)) ?? [];
}

export async function addToMutationQueue(mutation: Omit<OfflineMutation, "id" | "timestamp">): Promise<OfflineMutation> {
  const queue = await getMutationQueue();
  const entry: OfflineMutation = {
    ...mutation,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  };
  queue.push(entry);
  await set(MUTATION_QUEUE_KEY, queue);
  return entry;
}

export async function removeMutationFromQueue(id: string): Promise<void> {
  const queue = await getMutationQueue();
  await set(MUTATION_QUEUE_KEY, queue.filter((m) => m.id !== id));
}

export async function clearMutationQueue(): Promise<void> {
  await del(MUTATION_QUEUE_KEY);
}

// ===== Contact Cache (for offline viewing / optimistic updates) =====

const CONTACTS_CACHE_KEY = `${OFFLINE_PREFIX}contacts`;

export async function cacheContacts(contacts: unknown[]): Promise<void> {
  await set(CONTACTS_CACHE_KEY, contacts);
}

export async function getCachedContacts<T>(): Promise<T[]> {
  return (await get(CONTACTS_CACHE_KEY)) ?? [];
}
